<!-- Switcher between the Auftragsbegleitscheine of the same order. Outside the lock overlay's area, so a
     document locked by someone else can still be left for a sibling. -->
@if (showSiblingSelect()) {
  <!-- Full width on small screens, a quarter of the page from lg -->
  <div class="w-full lg:w-1/4 mb-4 flex flex-col gap-2">
    <label for="siblingSelect" class="text-sm font-medium text-text-secondary">Auftragsbegleitscheine</label>
    <p-select
      inputId="siblingSelect"
      [formControl]="siblingControl"
      [options]="siblingOptions()"
      optionLabel="label"
      optionValue="value"
      appendTo="body"
      styleClass="select-component-style w-full"
      panelStyleClass="select-component-panel-style"
    />
  </div>
}
<div class="w-full relative">
  @if (isLocked()) {
    <div class="absolute inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm rounded-t-2xl">
      <div class="p-6 rounded-2xl bg-info-light border border-info-border flex flex-col items-center gap-4 max-w-md text-center">
        <span class="material-icons text-info text-4xl">lock</span>
        <span class="text-info font-medium">
          @if (lockInfo()?.employeeId) {
            Dieser Auftragsbegleitschein wird bereits durch den Benutzer mit Mitarbeiter-Nr.: {{ lockInfo()?.employeeId }} bearbeitet. Bearbeitung ist
            nicht möglich.
          } @else {
            Dieser Auftragsbegleitschein wird bereits durch einen anderen Benutzer bearbeitet. Bearbeitung ist nicht möglich.
          }
        </span>
        <button type="button" class="btn-secondary mt-2" (click)="cancel()">Zurück zur Übersicht</button>
      </div>
    </div>
  }
  <form [formGroup]="form" (ngSubmit)="submit()">
    <!-- Main Info -->
    <div class="glass-card !z-40 p-6 rounded-2xl mb-6 relative">
      <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        @if (isEditMode()) {
          <!-- Kunde (order search) -->
          <div class="flex flex-col gap-2">
            <label for="order" class="text-sm font-medium text-text-secondary">Kunde</label>
            <p-autoComplete
              inputId="order"
              formControlName="order"
              [suggestions]="orderSuggestions()"
              (completeMethod)="searchOrders($event.query)"
              [minLength]="minOrderSearchLength"
              [delay]="250"
              optionLabel="companyName"
              [forceSelection]="true"
              [showClear]="true"
              [showEmptyMessage]="true"
              emptyMessage="Keine Ergebnisse"
              placeholder="Suche nach Auftrags-Nr. oder Kunde..."
              appendTo="body"
              styleClass="autocomplete-component-style"
              panelStyleClass="autocomplete-component-panel-style"
            >
              <ng-template let-order pTemplate="item">
                <div class="flex items-center justify-between gap-3">
                  <span class="font-medium text-text-primary">{{ order.internalOrderId }}</span>
                  <span class="text-text-secondary truncate">{{ order.companyName }}</span>
                </div>
              </ng-template>
            </p-autoComplete>
          </div>
        }

        @if (showTempCustomerField()) {
          <!-- Temporärer Kunde: optional, never required -->
          <div class="flex flex-col gap-2">
            <label for="tempCustomer" class="text-sm font-medium text-text-secondary">Temporärer Kunde</label>
            <input
              type="text"
              id="tempCustomer"
              formControlName="tempCustomer"
              class="w-full px-4 py-3 rounded-xl bg-white/8 border border-white/12 text-text-primary text-sm transition-all focus:outline-none focus:border-primary read-only:bg-white/4 read-only:cursor-not-allowed"
              placeholder="Kunde (frei)"
              appSelectOnFocus
            />
          </div>
        }

        <!-- Paletten-/Box-Typ + Nummer: required while creating (see the requiredWhileCreating validator effect) -->
        <div class="flex flex-col gap-2">
          <label for="palletNumber" class="text-sm font-medium text-text-secondary">Paletten- / Boxnummer{{ isEditMode() ? '' : ' *' }}</label>
          <div class="flex gap-2">
            <p-select
              inputId="palletBoxType"
              formControlName="palletBoxType"
              [options]="palletBoxTypeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Typ"
              [filter]="false"
              [showClear]="false"
              appendTo="body"
              styleClass="select-component-style flex-1 min-w-30"
              panelStyleClass="select-component-panel-style"
            >
              <ng-template pTemplate="item" let-option>
                <div class="option-row">
                  <span class="option-label">{{ option.label }}</span>
                </div>
              </ng-template>
            </p-select>
            <input
              type="text"
              id="palletNumber"
              placeholder="Nummer"
              formControlName="palletNumber"
              class="flex-1 min-w-0 px-4 py-3 rounded-xl bg-white/8 border text-text-primary text-sm transition-all focus:outline-none focus:border-primary read-only:bg-white/4 read-only:cursor-not-allowed"
              [ngClass]="isFieldInvalid('palletNumber') ? 'border-red-500 focus:border-red-500' : 'border-white/12'"
              appSelectOnFocus
            />
          </div>
          @if (isFieldInvalid('palletBoxType') || isFieldInvalid('palletNumber')) {
            <span class="text-xs text-red-500">Bitte Typ und Nummer angeben</span>
          }
        </div>

        <!-- WE Datum -->
        <div class="flex flex-col gap-2 w-full">
          <label for="weDate" class="text-sm font-medium text-text-secondary">WE Datum *</label>
          <p-datepicker
            inputId="weDate"
            formControlName="weDate"
            dateFormat="dd.mm.yy"
            [showIcon]="true"
            appendTo="body"
            styleClass="datepicker-component-style"
            panelStyleClass="datepicker-panel-style"
          ></p-datepicker>
          @if (isFieldInvalid('weDate')) {
            <span class="text-xs text-red-500">Bitte ein WE Datum wählen</span>
          }
        </div>

        <!-- Bauteilart + Gurtbreite share one grid cell so the header grid keeps its column count -->
        <div class="flex gap-4">
          <!-- Bauteilart: required while creating (see the requiredWhileCreating validator effect) -->
          <div class="flex flex-col gap-2 flex-1 min-w-0">
            <label for="componentType" class="text-sm font-medium text-text-secondary">Bauteilart{{ isEditMode() ? '' : ' *' }}</label>
            <input
              type="text"
              placeholder="Sensor"
              id="componentType"
              formControlName="componentType"
              class="w-full px-4 py-3 rounded-xl bg-white/8 border text-text-primary text-sm transition-all focus:outline-none focus:border-primary read-only:bg-white/4 read-only:cursor-not-allowed"
              [ngClass]="isFieldInvalid('componentType') ? 'border-red-500 focus:border-red-500' : 'border-white/12'"
              appSelectOnFocus
            />
            @if (isFieldInvalid('componentType')) {
              <span class="text-xs text-red-500">Bitte eine Bauteilart angeben</span>
            }
          </div>

          <!-- Gurtbreite: optional, never required -->
          <div class="flex flex-col gap-2 flex-1 min-w-0">
            <label for="gurtbreite" class="text-sm font-medium text-text-secondary">Gurtbreite</label>
            <p-select
              inputId="gurtbreite"
              formControlName="gurtbreite"
              [options]="gurtbreiteOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Wählen"
              [filter]="false"
              [showClear]="true"
              appendTo="body"
              styleClass="select-component-style"
              panelStyleClass="select-component-panel-style"
            >
              <ng-template pTemplate="item" let-option>
                <div class="option-row">
                  <span class="option-label">{{ option.label }}</span>
                </div>
              </ng-template>
            </p-select>
          </div>
        </div>

        <div class="flex gap-4">
          <!-- Auftragsmenge -->
          <div class="flex flex-col gap-2">
            <label for="orderQuantity" class="text-sm font-medium text-text-secondary">Auftragsmenge</label>
            <input
              type="text"
              placeholder="Auftragsmenge"
              id="orderQuantity"
              formControlName="orderQuantity"
              class="w-full px-4 py-3 rounded-xl bg-white/8 border border-white/12 text-text-primary text-sm transition-all focus:outline-none focus:border-primary read-only:bg-white/4 read-only:cursor-not-allowed"
              appSelectOnFocus
            />
          </div>

          <!-- Teilmenge -->
          <div class="flex flex-col gap-2">
            <label for="partialQuantity" class="text-sm font-medium text-text-secondary">Teilmenge</label>
            <input
              type="text"
              id="partialQuantity"
              placeholder="Teilmenge"
              formControlName="partialQuantity"
              class="w-full px-4 py-3 rounded-xl bg-white/8 border border-white/12 text-text-primary text-sm transition-all focus:outline-none focus:border-primary read-only:bg-white/4 read-only:cursor-not-allowed"
              appSelectOnFocus
            />
          </div>
        </div>

        <!-- Status -->
        <div class="flex flex-col gap-2">
          <label for="status" class="text-sm font-medium text-text-secondary">Status *</label>
          <p-select
            inputId="status"
            formControlName="status"
            [options]="statusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Status wählen"
            [filter]="false"
            [showClear]="false"
            appendTo="body"
            styleClass="select-component-style"
            panelStyleClass="select-component-panel-style"
          >
            <ng-template pTemplate="item" let-option>
              <div class="option-row">
                <span class="option-label">{{ option.label }}</span>
              </div>
            </ng-template>
          </p-select>
        </div>

        <!-- Werk -->
        <div class="flex flex-col gap-2">
          <label for="factory" class="text-sm font-medium text-text-secondary">Werk *</label>
          <p-select
            inputId="factory"
            formControlName="factory"
            [options]="werkOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Werk wählen"
            [filter]="false"
            [showClear]="false"
            appendTo="body"
            styleClass="select-component-style"
            panelStyleClass="select-component-panel-style"
          >
            <ng-template pTemplate="item" let-option>
              <div class="option-row">
                <span class="option-label">{{ option.label }}</span>
              </div>
            </ng-template>
          </p-select>
          @if (isFieldInvalid('factory')) {
            <span class="text-xs text-red-500">Bitte ein Werk wählen</span>
          }
        </div>

        <!-- Bearbeitung: required while creating (see the requiredWhileCreating validator effect) -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-text-secondary">Bearbeitung{{ isEditMode() ? '' : ' *' }}</label>
          <p-multiSelect
            formControlName="processing"
            [options]="processingOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Bearbeitung wählen"
            [filter]="false"
            [showClear]="true"
            [display]="'chip'"
            styleClass="multiselect-component-style"
            panelStyleClass="multiselect-component-panel-style"
          >
            <ng-template pTemplate="item" let-option>
              <div class="option-row">
                <span class="option-label">{{ option.label }}</span>
              </div>
            </ng-template>
          </p-multiSelect>
          @if (isFieldInvalid('processing')) {
            <span class="text-xs text-red-500">Bitte Radial oder SMD wählen</span>
          }
        </div>

        <!-- Tätigkeiten: required while creating (see the requiredWhileCreating validator effect) -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-text-secondary">Tätigkeiten{{ isEditMode() ? '' : ' *' }}</label>
          <p-multiSelect
            formControlName="activities"
            [options]="activityOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Tätigkeiten wählen"
            [filter]="false"
            [showClear]="true"
            [display]="'chip'"
            styleClass="multiselect-component-style"
            panelStyleClass="multiselect-component-panel-style"
          >
            <ng-template pTemplate="item" let-option>
              <div class="option-row">
                <span class="option-label">{{ option.label }}</span>
              </div>
            </ng-template>
          </p-multiSelect>
          @if (isFieldInvalid('activities')) {
            <span class="text-xs text-red-500">Bitte mindestens eine Tätigkeit wählen</span>
          }
        </div>

        <!-- Mitarbeiter-Nr.: confirmed in the Mitarbeiternummer modal when creating, never editable -->
        <div class="flex flex-col gap-2">
          <label for="employeeId" class="text-sm font-medium text-text-secondary">Mitarbeiter-Nr.</label>
          <div class="relative">
            <input
              type="text"
              id="employeeId"
              formControlName="employeeId"
              class="w-full px-4 py-3 rounded-xl bg-white/8 border border-white/12 text-text-primary text-sm transition-all focus:outline-none focus:border-primary read-only:bg-white/4 read-only:cursor-not-allowed"
              [class.pr-10]="isAdmin()"
              readonly
              tabindex="-1"
            />
            @if (isAdmin()) {
              <app-employee-info-button [employeeId]="form.get('employeeId')?.value" />
            }
          </div>
        </div>

        <!-- Spezifikation beachten -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-text-secondary">Spezifikation beachten</label>
          <div class="flex items-center h-[42px]">
            <p-toggleswitch formControlName="observeSpecification"></p-toggleswitch>
          </div>
        </div>
      </div>
      <div class="flex flex-col mt-6 md:flex-row gap-6">
        <!-- Bemerkung: always visible -->
        <div class="flex flex-col gap-2 !gap-4 justify-between flex-[1] min-w-0">
          <div class="flex items-center gap-2">
            <label for="remark" class="text-sm font-medium text-text-secondary">Bemerkung</label>
            <button
              type="button"
              class="text-text-muted cursor-pointer hover:text-text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title="Textbaustein einfügen"
              [disabled]="form.get('remark')?.disabled"
              (click)="openTextBlockModal()"
            >
              <span class="material-icons !text-xl">text_snippet</span>
            </button>
          </div>
          <textarea
            id="remark"
            formControlName="remark"
            class="w-full px-4 py-3 rounded-xl bg-white/8 border border-white/12 text-text-primary text-sm transition-all focus:outline-none focus:border-primary read-only:bg-white/4 read-only:cursor-not-allowed min-h-[74px] h-[stretch] resize-y"
            appSelectOnFocus
            placeholder="Bemerkung..."
          ></textarea>
        </div>

        @if (isEditMode()) {
          <!-- Liefertermine: hidden while creating (gated by isEditMode() above); every logged-in
                 user may add/edit/remove entries once in edit mode. -->
          <div class="flex flex-col gap-2 flex-1 min-w-0" [formGroup]="newLieferterminForm">
            <div class="flex items-center !justify-between">
              <label class="text-sm font-medium text-text-secondary">Liefertermine</label>
              <p-datepicker
                formControlName="date"
                dateFormat="dd.mm.yy"
                [showIcon]="true"
                appendTo="body"
                styleClass="liefertermine-add-picker-style"
                panelStyleClass="datepicker-panel-style"
                (onSelect)="addLiefertermin($event)"
              >
                <ng-template pTemplate="triggericon">
                  <span class="material-icons text-lg">add</span>
                </ng-template>
              </p-datepicker>
            </div>

            <div class="liefertermine-rows flex flex-col gap-2 max-h-44 overflow-y-auto" [class.error]="isFieldInvalid('deliveryDates')">
              @if (deliveryDates.length) {
                @for (group of sortedLiefertermine(); track group) {
                  <div class="flex items-center gap-2" [formGroup]="asFormGroup(group)">
                    <p-datepicker
                      formControlName="date"
                      dateFormat="dd.mm.yy"
                      [showIcon]="false"
                      appendTo="body"
                      styleClass="liefertermine-date-style"
                      panelStyleClass="datepicker-panel-style"
                    ></p-datepicker>

                    <input
                      type="text"
                      formControlName="label"
                      class="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg bg-white/8 border border-white/12 text-text-primary text-[0.8125rem] focus:outline-none focus:border-primary"
                      placeholder="Bezeichnung"
                      [attr.maxlength]="35"
                      appSelectOnFocus
                    />

                    <label class="flex items-center gap-1.5 whitespace-nowrap cursor-pointer">
                      <p-checkbox formControlName="isBinding" [binary]="true"></p-checkbox>
                      <span
                        class="px-2 py-0.5 rounded-full text-[0.6875rem] font-semibold uppercase tracking-[0.03em] transition-all"
                        [ngClass]="asFormGroup(group).get('isBinding')?.value ? 'bg-yellow-400/15 text-yellow-400' : 'text-text-muted'"
                      >
                        sicher
                      </span>
                    </label>

                    <button
                      type="button"
                      class="flex-none bg-transparent border-none text-text-muted cursor-pointer text-sm leading-none p-1 hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-text-muted"
                      [disabled]="isFertigungsmitarbeiter()"
                      (click)="removeLieferterminGroup(group)"
                    >
                      ✕
                    </button>
                  </div>
                }
              } @else {
                <p class="m-0 text-[0.8125rem] text-text-muted">Noch keine Liefertermine gesetzt.</p>
              }
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Zeiterfassung / Fehlererfassung / Dokumente tabs. Inactive panels are only hidden (not destroyed), so
         both FormArrays stay bound to the form whichever tab is open. -->
    <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)" [showNavigators]="false" class="order-document-tabs-style">
      <div class="glass-card rounded-2xl mb-6 overflow-hidden">
        <p-tablist>
          <p-tab value="positions">
            <span class="material-icons text-xl">schedule</span>
            <span>Zeiterfassung</span>
          </p-tab>
          <p-tab value="falsePositions">
            <span class="material-icons text-xl">error</span>
            <span>Fehlererfassung</span>
          </p-tab>
          <p-tab value="documents">
            <span class="material-icons text-xl">description</span>
            <span>Dokumente</span>
          </p-tab>
        </p-tablist>
      </div>

      <p-tabpanels>
        <!-- Zeiterfassung (positions) -->
        <p-tabpanel value="positions">
          <app-positions-table
            [positions]="positions"
            [saved]="isEditMode()"
            [hasCustomer]="hasCustomer()"
            [editable]="canManagePositions()"
            [canRecordFalsePosition]="canManageFalsePositions()"
            [isSubmitting]="isSubmitting()"
            [(headerPinned)]="isHeaderPinned"
            (recordFalsePosition)="recordFalsePosition($event)"
          />
        </p-tabpanel>

        <!-- Fehlererfassung (false positions): readable for every role, editable for ADMIN and ENDKONTROLLE -->
        <p-tabpanel value="falsePositions">
          <app-false-positions-table
            [falsePositions]="falsePositions"
            [saved]="isEditMode()"
            [hasCustomer]="hasCustomer()"
            [editable]="canManageFalsePositions()"
            [isSubmitting]="isSubmitting()"
          />
        </p-tabpanel>

        <!-- Dokumente: visible to and upload for every role, delete for ADMIN, BÜRO and WARENEINGANG -->
        <p-tabpanel value="documents">
          <app-documents
            [orderDocumentId]="orderDocumentId()"
            [orderId]="orderId()"
            [ownerLabels]="siblingLabels()"
            [active]="activeTab() === 'documents'"
          />
        </p-tabpanel>
      </p-tabpanels>
    </p-tabs>

    <!-- Actions -->
    <div class="flex items-center justify-end gap-4">
      <button type="button" class="btn-secondary" (click)="cancel()">Schließen</button>
      @if (canSave()) {
        <button type="submit" class="btn-primary px-6 py-3" [disabled]="isSubmitting() || isLockLoading()">
          @if (isSubmitting()) {
            <span class="flex items-center gap-2">
              <span class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
              Speichern...
            </span>
          } @else {
            <span class="flex items-center gap-2">
              <span class="material-icons text-lg">save</span>
              {{ isEditMode() ? 'Speichern' : 'Erstellen' }}
            </span>
          }
        </button>
      }
    </div>
  </form>
</div>
